import { useState, useMemo } from 'react';
import { loadStripe, type Stripe } from '@stripe/stripe-js';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { formatBookingCurrency } from '../../utils/bookingFormat';
import BookingSummaryRow from './BookingSummaryRow';

export interface BookingAmounts {
	serviceTotal: number;
	deposit: number;
	supportFee: number;
	totalDue: number;
	balance: number;
}

interface BookingPaymentProps {
	publishableKey: string;
	/** PaymentIntent secret from the create call — identifies what is charged. */
	clientSecret: string;
	amounts: BookingAmounts;
	onBack: () => void;
	onPaid: () => void;
}

// `loadStripe` injects a script tag, so it must run once per key rather than on
// every render. Keyed because stage and production publish different keys.
const stripePromises = new Map<string, Promise<Stripe | null>>();

const getStripe = (publishableKey: string): Promise<Stripe | null> => {
	const cached = stripePromises.get(publishableKey);
	if (cached) return cached;
	const promise = loadStripe(publishableKey);
	stripePromises.set(publishableKey, promise);
	return promise;
};

/**
 * Status of an existing PaymentIntent.
 *
 * Used when the customer comes back from an off-site step such as 3-D Secure:
 * the deposit may already be paid, and the page has to find that out before
 * showing anything that invites a second attempt.
 */
export const retrievePaymentStatus = async (
	publishableKey: string,
	clientSecret: string
): Promise<string | null> => {
	const stripe = await getStripe(publishableKey);
	if (!stripe) return null;
	const { paymentIntent } = await stripe.retrievePaymentIntent(clientSecret);
	return paymentIntent?.status ?? null;
};

/**
 * Step 6 of the paid booking flow: the fee breakdown, Stripe's card form, the
 * consent checkboxes and the pay button.
 *
 * Card details are collected by Stripe's own PaymentElement rather than the
 * plain inputs the mockup shows. Raw card numbers must never touch our DOM —
 * routing them through our own fields would put this app in PCI DSS scope.
 */
const BookingPayment = ({
	publishableKey,
	clientSecret,
	amounts,
	onBack,
	onPaid,
}: BookingPaymentProps) => {
	const stripePromise = useMemo(() => getStripe(publishableKey), [publishableKey]);

	return (
		<Elements
			stripe={stripePromise}
			options={{
				clientSecret,
				appearance: {
					theme: 'flat',
					variables: {
						colorPrimary: '#8b5e3c',
						colorText: '#1a1a1a',
						colorDanger: '#c0392b',
						borderRadius: '10px',
						fontSizeBase: '15px',
					},
				},
			}}
		>
			<PaymentPanel amounts={amounts} onBack={onBack} onPaid={onPaid} />
		</Elements>
	);
};

const PaymentPanel = ({
	amounts,
	onBack,
	onPaid,
}: {
	amounts: BookingAmounts;
	onBack: () => void;
	onPaid: () => void;
}) => {
	const stripe = useStripe();
	const elements = useElements();

	const [agreedCancellation, setAgreedCancellation] = useState(false);
	const [agreedTerms, setAgreedTerms] = useState(false);
	const [showFeeInfo, setShowFeeInfo] = useState(false);
	const [isPaying, setIsPaying] = useState(false);
	const [error, setError] = useState<string | null>(null);

	const canPay = agreedCancellation && agreedTerms && Boolean(stripe && elements) && !isPaying;

	const handlePay = async () => {
		if (!stripe || !elements) return;
		setIsPaying(true);
		setError(null);
		try {
			// `if_required` keeps the customer on the page for plain cards and
			// only redirects for methods that genuinely need it (3DS, wallets).
			const result = await stripe.confirmPayment({
				elements,
				redirect: 'if_required',
				confirmParams: { return_url: window.location.href },
			});

			if (result.error) {
				setError(result.error.message || 'Your payment could not be completed.');
				return;
			}

			const status = result.paymentIntent?.status;
			if (status === 'succeeded' || status === 'processing') {
				onPaid();
			} else {
				setError('Your payment was not completed. Please try again.');
			}
		} catch {
			setError('Something went wrong while processing your payment. Please try again.');
		} finally {
			setIsPaying(false);
		}
	};

	const payButton = (
		<button
			type="button"
			className="pb__btn pb__btn--success"
			onClick={handlePay}
			disabled={!canPay}
		>
			{isPaying ? 'Processing…' : `💳 Pay ${formatBookingCurrency(amounts.totalDue)}`}
		</button>
	);

	const backButton = (
		<button
			type="button"
			className="pb__btn pb__btn--secondary"
			onClick={onBack}
			disabled={isPaying}
		>
			← Back
		</button>
	);

	return (
		<>
			<div className="pb__actions pb__actions--top">
				{backButton}
				{payButton}
			</div>

			<div className="pb__alert">
				<span className="pb__alert-icon">💳</span>
				<div className="pb__alert-body">
					A deposit is required to secure your appointment. The remaining balance is due
					on the day of your service.
				</div>
			</div>

			{error && (
				<div className="pb__error-banner">
					<span>⚠️</span>
					<span>{error}</span>
				</div>
			)}

			{/* Itemised fee breakdown */}
			<div className="pb-paybreak">
				<div className="pb-paybreak__heading">Payment Breakdown</div>
				<div className="pb-paybreak__group-label">Customer Charges</div>

				<div className="pb__summary">
					<BookingSummaryRow
						label="Service Price"
						value={formatBookingCurrency(amounts.serviceTotal)}
					/>
					<BookingSummaryRow
						label="Required Deposit"
						value={formatBookingCurrency(amounts.deposit)}
					/>

					<span className="pb__summary-label pb-paybreak__fee-label">
						Support Fee
						<span className="pb-paybreak__nonrefundable">Non-Refundable</span>
						<button
							type="button"
							className="pb-paybreak__info-toggle"
							onClick={() => setShowFeeInfo(open => !open)}
							aria-expanded={showFeeInfo}
							title="Learn more about the Support Fee"
						>
							i
						</button>
					</span>
					<span className="pb__summary-value">
						{formatBookingCurrency(amounts.supportFee)}
					</span>

					{showFeeInfo && (
						<div className="pb-paybreak__info">
							<strong>ℹ️ About the Support Fee</strong>
							The Support Fee is a non-refundable platform fee that covers the cost of
							operating the Beauty Business Portal (BBP) booking system, securing your
							reservation, and processing any applicable payment transactions. This
							fee is assessed based on your total service price and is collected at
							the time of booking. It is not returned in the event of a cancellation
							or no-show.
						</div>
					)}
				</div>

				<div className="pb-paybreak__total">
					<div className="pb-paybreak__total-label">Total Due Today</div>
					<div className="pb-paybreak__total-value">
						{formatBookingCurrency(amounts.totalDue)}
					</div>
				</div>

				<div className="pb-paybreak__balance">
					<div className="pb-paybreak__balance-label">
						Remaining Balance Due at Appointment
					</div>
					<div className="pb-paybreak__balance-value">
						{formatBookingCurrency(amounts.balance)}
					</div>
				</div>
			</div>

			<div className="pb__divider" />
			<div className="pb__section-label">Card Details</div>

			<div className="pb-stripe">
				{!stripe || !elements ? (
					<div className="pb-stripe__loading">
						<span className="pb__spinner" />
						Loading secure payment form…
					</div>
				) : null}
				<PaymentElement options={{ layout: 'tabs' }} />
			</div>

			{/* Consent & agreement */}
			<div className="pb-consent">
				<div className="pb-consent__heading">📋 Consent &amp; Agreement</div>

				<label className="pb-consent__row">
					<input
						type="checkbox"
						checked={agreedCancellation}
						onChange={event => setAgreedCancellation(event.target.checked)}
					/>
					<span className="pb-consent__text">
						I understand and agree to the <strong>48-Hour Cancellation Policy</strong>.
						Cancellations made less than 48 hours before the appointment may result in
						forfeiture of the deposit. No-shows will be charged the full deposit amount.
					</span>
				</label>

				<label className="pb-consent__row">
					<input
						type="checkbox"
						checked={agreedTerms}
						onChange={event => setAgreedTerms(event.target.checked)}
					/>
					<span className="pb-consent__text">
						I agree to the <a href="#terms">Terms of Service</a> and{' '}
						<a href="#privacy">Privacy Policy</a>. I authorize the charge of the deposit
						amount listed above to the card provided.
					</span>
				</label>
			</div>

			<div className="pb__actions">
				{backButton}
				{payButton}
			</div>
		</>
	);
};

export default BookingPayment;
